import type { Zone, ZoningRule } from "@/graphql"
import { formatQuantity } from "@/utils/format"

type Translate = (key: string, values?: Record<string, unknown>) => string

interface ZoningRow {
	label: string
	value: string
}

interface ZoningBlock {
	title: string
	rows: ZoningRow[]
}

interface ZoneDetails {
	zoneCode: string
	rows: ZoningRow[]
	gridUrl: string | null
	blocks: ZoningBlock[]
}

function formatNumber(value: number, unit = ""): string {
	return `${formatQuantity(value, { maximumFractionDigits: 2 })}${unit}`
}

function formatRange(
	t: Translate,
	min: number | null | undefined,
	max: number | null | undefined,
	unit = ""
): string | null {
	if (min != null && max != null) {
		return min === max
			? formatNumber(max, unit)
			: t("zoning.range", { min: formatNumber(min, unit), max: formatNumber(max, unit) })
	}
	if (min != null) return t("zoning.atLeast", { value: formatNumber(min, unit) })
	if (max != null) return t("zoning.atMost", { value: formatNumber(max, unit) })
	return null
}

function formatValue(value: number | null | undefined, unit = ""): string | null {
	return value == null ? null : formatNumber(value, unit)
}

function distinctValues(values: Array<string | null | undefined>): string | null {
	const items = values.flatMap(value => value?.split(",") ?? []).map(item => item.trim())
	const distinct = [...new Set(items.filter(Boolean))]
	return distinct.length ? distinct.join(", ") : null
}

function presentRows(rows: Array<{ label: string; value: string | null | undefined }>): ZoningRow[] {
	return rows
		.filter((row): row is ZoningRow => Boolean(row.value))
		.map(row => ({ ...row, value: row.value.replace(/\s*\|\s*/g, " | ") }))
}

function useClassLabel(rule: ZoningRule): string | null {
	return rule.useClassDescription ?? rule.useClass ?? null
}

function ruleSummary(rule: ZoningRule): string[] {
	return [rule.structureType, rule.useGroup, useClassLabel(rule)].filter((part): part is string => Boolean(part))
}

function ruleBlock(rule: ZoningRule, t: Translate): ZoningBlock | null {
	const summary = ruleSummary(rule)
	const rows = ruleRows(rule, t)
	if (summary.length === 0 && rows.length === 0) return null
	return { title: summary.length ? summary.join(" · ") : t("zoning.norms"), rows }
}

function zoneRows(zone: Zone, t: Translate): ZoningRow[] {
	const pick = <Key extends keyof ZoningRule>(key: Key) =>
		zone.rules.map(rule => rule[key] as string | null | undefined)
	return presentRows([
		{ label: t("zoning.zone"), value: zone.zoneCode },
		{ label: t("zoning.borough"), value: zone.borough },
		{ label: t("zoning.bylaw"), value: distinctValues(pick("bylawNumber")) },
		{ label: t("zoning.printDate"), value: distinctValues(pick("printDate")) },
		{ label: t("zoning.useGroups"), value: distinctValues(pick("useGroup")) },
		{ label: t("zoning.useClasses"), value: distinctValues(zone.rules.map(useClassLabel)) },
		{ label: t("zoning.specialProvisions"), value: distinctValues(pick("specialProvisionCodes")) },
		{ label: t("zoning.otherProvisions"), value: distinctValues(pick("otherProvisionCodes")) },
	])
}

function ruleRows(rule: ZoningRule, t: Translate): ZoningRow[] {
	const principalUse = rule.isPrincipalUse == null ? null : t(rule.isPrincipalUse ? "parcelCard.yes" : "parcelCard.no")
	return presentRows([
		{ label: t("zoning.principalUse"), value: principalUse },
		{ label: t("zoning.permittedUses"), value: rule.permittedUseCodes },
		{ label: t("zoning.excludedUses"), value: rule.excludedUseCodes },
		{
			label: t("zoning.heightFloors"),
			value: formatRange(t, rule.minHeightFloors, rule.maxHeightFloors),
		},
		{
			label: t("zoning.heightMeters"),
			value: formatRange(t, rule.minHeightMeters, rule.maxHeightMeters, " m"),
		},
		{
			label: t("zoning.buildingWidth"),
			value: formatRange(t, rule.minBuildingWidthMeters, rule.maxBuildingWidthMeters, " m"),
		},
		{
			label: t("zoning.footprint"),
			value: formatRange(t, rule.minFootprintSquareMeters, rule.maxFootprintSquareMeters, " m²"),
		},
		{
			label: t("zoning.floorArea"),
			value: formatRange(t, rule.minFloorAreaSquareMeters, rule.maxFloorAreaSquareMeters, " m²"),
		},
		{
			label: t("zoning.unitsPerBuilding"),
			value: formatRange(t, rule.minUnitsPerBuilding, rule.maxUnitsPerBuilding),
		},
		{
			label: t("zoning.frontSetback"),
			value: formatRange(t, rule.minFrontSetbackMeters, rule.maxFrontSetbackMeters, " m"),
		},
		{
			label: t("zoning.sideStreetSetback"),
			value: formatRange(t, rule.minSideStreetSetbackMeters, rule.maxSideStreetSetbackMeters, " m"),
		},
		{ label: t("zoning.sideSetback"), value: formatValue(rule.sideSetbackMeters, " m") },
		{ label: t("zoning.totalSideSetback"), value: formatValue(rule.totalSideSetbackMeters, " m") },
		{ label: t("zoning.rearSetback"), value: formatValue(rule.rearSetbackMeters, " m") },
		{ label: t("zoning.netDensity"), value: formatRange(t, rule.minNetDensity, rule.maxNetDensity) },
		{
			label: t("zoning.coverageRatio"),
			value: formatRange(t, rule.minCoverageRatio, rule.maxCoverageRatio),
		},
		{
			label: t("zoning.floorAreaRatio"),
			value: formatRange(t, rule.minFloorAreaRatio, rule.maxFloorAreaRatio),
		},
		{ label: t("zoning.minLotWidth"), value: formatValue(rule.minLotWidthMeters, " m") },
		{ label: t("zoning.minLotDepth"), value: formatValue(rule.minLotDepthMeters, " m") },
		{ label: t("zoning.minLotArea"), value: formatValue(rule.minLotAreaSquareMeters, " m²") },
		{ label: t("zoning.specialProvisions"), value: rule.specialProvisionCodes },
		{ label: t("zoning.otherProvisions"), value: rule.otherProvisionCodes },
	])
}

export function zoneDetails(zones: Zone[], t: Translate): ZoneDetails[] {
	return zones.map(zone => ({
		zoneCode: zone.zoneCode,
		rows: zoneRows(zone, t),
		gridUrl: zone.documentUrl ?? zone.gridUrl ?? null,
		blocks: zone.rules.map(rule => ruleBlock(rule, t)).filter((block): block is ZoningBlock => block !== null),
	}))
}
